import { EntityTable } from '@/components/table/entity-table';
import { useAllSchools } from '@/lib/api-call-hooks';
import {
  feedbacksPageColumns,
  FeedbacksTableColumnIds,
  sessionFeedbacksPageColumns,
  studentFeedbacksPageColumns,
  teacherFeedbacksPageColumns,
} from '@/routes/_layout/sessions/-components/feedbacks-page-columns';
import { FrequentTimeZones } from '@airreading/common';
import { SessionFeedbackWithAssessment } from '@airreading/common/data';
import { getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ComponentProps, useMemo } from 'react';

type EntityTableProps = ComponentProps<typeof EntityTable>;

interface FeedbacksTableProps {
  sessionFeedbacks: SessionFeedbackWithAssessment[];
  filters?: EntityTableProps['filters'];
  emptyState?: EntityTableProps['emptyState'];
  tableHookProps?: Omit<TableOptions<SessionFeedbackWithAssessment>, 'columns' | 'data' | 'getCoreRowModel'>;
  bulkActionsMenuItems?: EntityTableProps['bulkActionsMenuItems'] | undefined;
  isLoading?: boolean;
  from?: 'teacher' | 'session' | 'student';
  totalRowCount?: number;
  isSelectAllPagesActive?: boolean;
  onSelectAllPagesChange?: (isActive: boolean) => void;
  tablePrefix?: string;
  bulkActionsTestId?: string;
}

export const FeedbacksTable = ({
  sessionFeedbacks,
  filters,
  tableHookProps,
  emptyState,
  bulkActionsMenuItems,
  isLoading,
  from,
  totalRowCount,
  isSelectAllPagesActive,
  onSelectAllPagesChange,
  tablePrefix,
  bulkActionsTestId,
}: FeedbacksTableProps) => {
  const { data: allSchools } = useAllSchools();
  const schoolIdToDistrictName = Object.fromEntries(allSchools.map((school) => [school.id, school.org.name]));

  const columns = useMemo(() => {
    switch (from) {
      case 'teacher':
        return teacherFeedbacksPageColumns(
          FrequentTimeZones.UsCentral,
          schoolIdToDistrictName,
          !!bulkActionsMenuItems,
          isSelectAllPagesActive,
          onSelectAllPagesChange,
        );
      case 'session':
        return sessionFeedbacksPageColumns(
          FrequentTimeZones.UsCentral,
          schoolIdToDistrictName,
          !!bulkActionsMenuItems,
          isSelectAllPagesActive,
          onSelectAllPagesChange,
          tablePrefix,
        );
      case 'student':
        return studentFeedbacksPageColumns(
          FrequentTimeZones.UsCentral,
          schoolIdToDistrictName,
          !!bulkActionsMenuItems,
          isSelectAllPagesActive,
          onSelectAllPagesChange,
          tablePrefix,
        );
      default:
        return feedbacksPageColumns(
          FrequentTimeZones.UsCentral,
          schoolIdToDistrictName,
          !!bulkActionsMenuItems,
          isSelectAllPagesActive,
          onSelectAllPagesChange,
        );
    }
  }, [from, bulkActionsMenuItems, schoolIdToDistrictName, isSelectAllPagesActive, onSelectAllPagesChange, tablePrefix]);

  const table = useReactTable({
    data: sessionFeedbacks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    ...tableHookProps,
    defaultColumn: {
      enableSorting: false,
      ...tableHookProps?.defaultColumn,
    },
    state: {
      ...tableHookProps?.state,
    },
  });

  return (
    <EntityTable
      table={table}
      emptyState={emptyState ?? 'No feedback found'}
      filters={filters}
      isLoading={isLoading}
      showPagination={true}
      bulkActionsMenuItems={bulkActionsMenuItems}
      isSelectAllPagesActive={isSelectAllPagesActive}
      onSelectAllPagesChange={onSelectAllPagesChange}
      totalRowCount={totalRowCount}
      bulkActionsTestId={bulkActionsTestId}
      autoManagedColumnIds={['selection', FeedbacksTableColumnIds.viewDetailsAdmin]}
    />
  );
};
